// React, services, context and routing
import { useAbility } from "@casl/react";
import { AbilityContext } from "Can";
import { AuthContext } from "context";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import crudsService from "services/cruds-service";
import productService from "services/product-service";

// E4A React components
import Footer from "e4acomponents/Footer";
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import DataTable from "e4acomponents/Tables/DataTable";

// Material Dashboard and MUI components
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import VisibilityTwoToneIcon from "@mui/icons-material/VisibilityTwoTone";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Fab from '@mui/material/Fab';
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";

// Fulfillment center components
import FiltersDialog from "../components/filters-dialog";
import { convertDate } from "../components/helperFunctions";
import RequestDispatchDialog from "../components/request-dispatch-dialog";
import ConfirmDialog from "e4acomponents/ConfirmDialog";

export default function ViewYourProducts() {
    // State management
    const [isLoading, setIsLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [user, setUser] = useState({});
    const [organization, setOrganization] = useState({});
    const [tableData, setTableData] = useState([]);
    const [showFiltersDialog, setShowFiltersDialog] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [openDispatchDialog, setOpenDispatchDialog] = useState(false);
    const [openAlert, setOpenAlert] = useState(false);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [dialogProps, setDialogProps] = useState({
        title: "Delete Product",
        prompt: "Are you sure you want to delete this product? This action cannot be undone."
    });

    // Contexts and routing
    const authContext = useContext(AuthContext);
    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();

    // Fetch products 
    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                //  Fetch current user's info
                const userId = await authContext.getCurrentUser();
                const currentUser = await crudsService.getUser(userId);
                setUser(currentUser.data);

                let products = [];
                const org = currentUser.data.relationships.organizations;
                if (org) { // If user has an organization associated
                    setOrganization(currentUser.data.relationships.organizations.data[0]);
                    const productsResponse = await productService.getOrgProducts(org.data[0].id);
                    products = productsResponse.data;

                } else { // If user has no organization associated
                    const productsResponse = await productService.getAllProducts();
                    products = productsResponse.data;

                }
                setProducts(products);

            } catch (error) {
                console.error("Error fetching products: ", error);
            } finally {
                setIsLoading(false);
            }
        })();
    }, []);

    // Update table's rows at the slightest change
    useEffect(() => {
        setTableData(getRows(products));
    }, [products]);

    // Formats data for the DataTable
    const getRows = (info) => {
        let updatedInfo = info
            .filter((e) => String(e.status || "").toLowerCase() !== "dispatched")
            .map((e) => {
                return {
                    id: e._id,
                    serialNumber: e.serialNumber,
                    firmwareVersion: e.firmwareVersion,
                    dateOfStorage: convertDate(e.dateOfStorage),
                    shelfLife: `${e.shelfLife[0]} ${e.shelfLife[1]}`,
                    organizationName: e.organization?.name || "",
                    statusChip: statusChip(e.status),
                    status: e.status,
                }
            })
        return updatedInfo;
    }

    // 1. Calculate Logic: Do we have permission? Do any rows need a checkbox?
    const hasEditPermission = ability.can('request', 'dispatches'); // or however you verify permission
    const hasActionableRows = tableData.some(row =>
        row.status === "Stored" || row.status === "Dispatch Canceled"
    );

    const showCheckboxColumn = hasEditPermission && hasActionableRows;

    const dataTableData = {
        columns: [
            // 2. Conditionally add the checkbox column
            ...(showCheckboxColumn ? [{
                Header: "",
                accessor: "selection_checkbox", // Give it a unique ID (don't use " " space)
                width: 50, // 3. Use NUMBER for fixed width (supported by new DataTable.js)
                disableSortBy: true,
                Cell: ({ row }) => (
                    (row.original.status === "Stored" || row.original.status === "Dispatch Canceled") && (
                        <Checkbox
                            size="small"
                            checked={selectedProducts.includes(row.original.id)}
                            onChange={() => handleSelectProduct(row.original.id)}
                        />
                    )
                )
            }] : []),

            { Header: "Serial Number", accessor: "serialNumber" },
            { Header: "Firmware Version", accessor: "firmwareVersion" },
            { Header: "Date of Storage", accessor: "dateOfStorage" },
            { Header: "Shelf Life", accessor: "shelfLife" },
            ...(!organization?.id ?
                [{
                    Header: "Organization",
                    accessor: "organizationName"
                }] : []
            ),
            {
                Header: "Status", accessor: "status",
                Cell: ({ row }) => row.original.statusChip,
                type: "singleSelect",
                valueOptions: ["Stored", "Requested", "Scheduled", "Dispatch Canceled"],
            },
            {
                Header: "Actions",
                accessor: "actions",
                disableSortBy: true,
                Cell: ({ row }) => {
                    return (
                        <MDBox display="flex" alignItems="center">
                            {ability.can('delete', 'product') && (
                                <Tooltip title="Delete Product">
                                    <IconButton
                                        onClick={() => handleOpenConfirmDialog(row.original.id)}
                                    >
                                        <DeleteTwoToneIcon color="error" />
                                    </IconButton>
                                </Tooltip>
                            )}

                            {ability.can('access', 'edit-product') && (
                                <Tooltip title="Edit Product">
                                    <IconButton
                                        onClick={() =>
                                            navigate(`/fulfillment-center/edit-product/${row.original.id}`)}
                                    >
                                        <EditTwoToneIcon color="success" />
                                    </IconButton>
                                </Tooltip>
                            )}

                            <Tooltip title="View Product">
                                <IconButton onClick={() => navigate(`/fulfillment-center/view-product/${row.original.id}`)}>
                                    <VisibilityTwoToneIcon color="info" />
                                </IconButton>
                            </Tooltip>
                        </MDBox>
                    )
                }
            }
        ],
        rows: tableData,
    }

    // Opens the filters dialog
    const openFiltersDialog = () => {
        setShowFiltersDialog(true);
    }
    // Closes the filters dialog
    const closeFiltersDialog = () => {
        setShowFiltersDialog(false);
    }

    // Opens the dispatch dialog
    const handleOpenDispatchDialog = () => {
        // Ensure all selected products belong to the same organization
        if (organization?.id) {
            setSelectedOrg(organization);
            setOpenDispatchDialog(true);
        } else {
            const selectedProductOrgs = selectedProducts.map((productId) => {
                const product = products.find((p) => p._id === productId);
                return product ? product.organization._id : null;
            });
            if (selectedProductOrgs.length > 0 && new Set(selectedProductOrgs).size === 1) {
                setSelectedOrg({ id: selectedProductOrgs[0] });
                setOpenDispatchDialog(true);
            } else {
                setOpenAlert(true);
            }
        }
    }

    // Closes the dispatch dialog
    const handleCloseDispatchDialog = () => {
        setOpenDispatchDialog(false);
        setIsLoading(true);
        (async () => {
            try {
                const list = organization?.id
                    ? (await productService.getOrgProducts(organization.id)).data
                    : (await productService.getAllProducts()).data;

                setProducts(list);
                setTableData(getRows(list));
                setSelectedProducts([]);
            } catch (err) {
                console.error("Error refreshing products: ", err);
            } finally {
                setIsLoading(false);
            }
        })();
    }

    // Applies the dialogs filters to the dataTable
    const applyFilters = (filters) => {
        setShowFiltersDialog(false);
        setTableData(
            getRows(
                products.
                    filter((product) => product.organization._id ?
                        filters.includes(product.organization._id) : false
                    )
            ));
    }

    // Handles selecting/deselecting products
    const handleSelectProduct = (productId) => {
        setSelectedProducts((prevSelected) => {
            if (prevSelected.includes(productId)) {
                return prevSelected.filter((id) => id !== productId);
            }
            return [...prevSelected, productId];
        });
    };

    const handleDeleteProduct = async (productId) => {
        try {
            const userId = await authContext.getCurrentUser();
            await productService.deleteProduct(productId, userId);
            setProducts((prevProducts) =>
                prevProducts.filter((product) => product._id !== productId)
            );
        } catch (error) {
            console.error("Error deleting product: ", error);
        }
    }

    const handleOpenConfirmDialog = (id) => {
        setDialogProps({
            title: "Delete Product",
            prompt: "Are you sure you want to delete this product? This action cannot be undone.",
            id: id,
            onClose: handleCloseConfirmDialog
        });
        setOpenConfirmDialog(true);
    }

    const handleCloseConfirmDialog = (response, title, id) => {
        setOpenConfirmDialog(false);
        if (response && title === "Delete Product") {
            handleDeleteProduct(id);
        }
    }

    // Renders a status chip based on the product's status
    const statusChip = (status) => {
        const normalized = String(status || "").toLowerCase();
        if (normalized === "dispatched") return null;

        const map = {
            stored: { color: "info" },
            requested: { color: "warning" },
            scheduled: { color: "success" },
            "dispatch canceled": { color: "error" },
        };
        const variant = map[normalized];
        if (!variant) return null;
        return (
            <Chip variant="outlined" size="small" label={status} color={variant.color} />
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <Snackbar
                open={openAlert}
                autoHideDuration={1500}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                onClose={() => setOpenAlert(false)}
            >
                <Alert severity="error" >
                    Cannot request dispatch for products under different organizations.
                </Alert>
            </Snackbar>
            <MDBox pt={6} pb={3}>

                {/* Request Dispatch Dialog */}
                <RequestDispatchDialog
                    open={openDispatchDialog}
                    handleClose={handleCloseDispatchDialog}
                    products={selectedProducts}
                    organization={organization.id ? organization : selectedOrg}
                />

                {/* Dialog for filtering by organization */}
                <FiltersDialog
                    handleClose={closeFiltersDialog}
                    open={showFiltersDialog}
                    submit={applyFilters}
                />

                {/* Confirm Dialog used for deletions */}
                <ConfirmDialog
                    open={openConfirmDialog}
                    props={dialogProps}
                />

                <Card>
                    <MDBox p={3} lineHeight={1} display="flex" justifyContent="space-between">

                        {/* Title */}
                        <MDTypography variant="h5" fontWeight="medium">
                            {!(organization?.id) ?
                                "Client products" :
                                `Your Products (${organization?.label})`}
                        </MDTypography>

                        {/* Add New Product Button */}
                        {ability.can('access', 'add-product') && (
                            <MDButton
                                variant="gradient"
                                color="info"
                                size="small"
                                type="submit"
                                onClick={() => navigate("/fulfillment-center/add-product")}
                            >
                                Add Product
                            </MDButton>
                        )}

                    </MDBox>
                    {isLoading ? (
                        // Loading Spinner
                        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                            <CircularProgress />
                        </MDBox>

                    ) : (
                        <>
                            <MDBox
                                sx={{
                                    position: "fixed",
                                    bottom: 24,
                                    right: 24,
                                    transform: selectedProducts.length > 0 ? "scale(1)" : "scale(0)",
                                    transition: "transform 300ms ease",
                                    zIndex: 1400,
                                }}
                            >
                                <MDBox
                                    component="span"
                                    sx={{
                                        mR: 2,
                                        backgroundColor: "#fff",
                                        color: "#000",
                                        borderRadius: "50%",
                                        padding: "4px 8px",
                                        fontSize: "16px",
                                        fontWeight: "bold",
                                        ...(openDispatchDialog ? {
                                            backgroundColor: "rgba(128, 128, 128, 0.6)",
                                            color: "#111",
                                        } : {}),
                                    }}
                                >
                                    {selectedProducts.length}
                                </MDBox>

                                <Fab
                                    variant="extended" size="large" color="primary"
                                    onClick={handleOpenDispatchDialog}
                                    disabled={openDispatchDialog}
                                >
                                    <LocalShippingIcon sx={{ mr: 1 }} />
                                    Request Dispatch
                                </Fab>
                            </MDBox>

                            {/* DataTable */}
                            <MDBox
                                sx={{
                                    "& table": {
                                        "& tbody tr": {
                                            transition: "background-color 0.2s ease",
                                        },
                                        // Highlight rows that contain a checked checkbox, regardless of order
                                        "& tbody tr:has(input[type='checkbox']:checked)": {
                                            backgroundColor: "#e3f2fd !important",
                                            "&:hover": {
                                                backgroundColor: "#bbdefb !important",
                                            },
                                        },
                                        // Default hover for non-selected rows
                                        "& tbody tr:not(:has(input[type='checkbox']:checked')):hover": {
                                            backgroundColor: "#f5f5f5 !important",
                                        },
                                    },
                                }}
                            >
                                {organization?.id ?
                                    (
                                        <DataTable
                                            table={dataTableData}
                                            canSearch={true}
                                            tableId="your-products-table-1"
                                        />
                                    ) : (
                                        <DataTable
                                            table={dataTableData}
                                            canSearch={true}
                                            hasFilters
                                            openFiltersModal={openFiltersDialog}
                                            tableId="your-products-table-2"
                                        />
                                    )}
                            </MDBox>
                        </>
                    )}
                </Card>

            </MDBox >
            <Footer />
        </DashboardLayout >
    );
}